import { useState, useEffect, useCallback } from 'react';
import { HStack, VStack } from '@terra/ui-styled-system';
import {
  Button,
  ConfirmationDialog,
  SettingsBooleanControl,
  SettingsRow,
  toasterApi,
  useBranding,
} from '@terra/ui-design-system';
import { Add01Icon, SecurityCheckIcon, ShieldBanIcon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { useSettingsHelpers } from '../hooks/useSettingsHelpers';
import { useElectronAPI } from '../hooks/useElectronAPI';
import { useProxyActions } from '../hooks/useProxyActions';
import { ProxySettingsGroup } from './settings/proxy-settings/ProxySettingsGroup';
import { ProxySettingEditRow } from './settings/proxy-settings/ProxySettingEditRow';
import { renderProxySettingsListRows } from './settings/proxy-settings/ProxySettingsList';
import { ProxyValueAddDialog } from './settings/proxy-settings/ProxyValueAddDialog';

const CERTIFICATE_STATUS_RECHECK_DELAY_MS = 2000;

type HostSetting = 'allowedHosts' | 'blockedHosts';

export function SecurityTab() {
  const t = useTranslations('settings.proxyForm.security');
  const tCommon = useTranslations('common');
  const tSecurity = useTranslations('settings.proxySecurityActions');
  const { brandName } = useBranding();
  const {
    settings,
    updateSetting,
    updateNestedSetting,
    addToArraySetting,
    removeFromArraySetting,
  } = useSettingsHelpers();
  const { removeCACertificate, isCACertificateInstalled } = useElectronAPI();
  const { installCACertificate, isLoading: isInstallingCert } =
    useProxyActions();
  const [isRemovingCert, setIsRemovingCert] = useState(false);
  const [isCertInstalled, setIsCertInstalled] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [hostDialogSetting, setHostDialogSetting] =
    useState<HostSetting | null>(null);
  const checkCertStatus = useCallback(async () => {
    try {
      const result = await isCACertificateInstalled();

      setIsCertInstalled(result.success && result.data?.installed === true);
    } catch (error) {
      console.error('Failed to check certificate status:', error);
    }
  }, [isCACertificateInstalled]);

  useEffect(() => {
    void checkCertStatus();
  }, [checkCertStatus]);

  const confirmRemoveCertificate = async () => {
    setShowConfirmDialog(false);
    setIsRemovingCert(true);

    try {
      const result = await removeCACertificate();

      if (result.success) {
        toasterApi.success({
          title: t('removedTitle'),
          description: t('removedDescription', { brandName }),
        });
        await checkCertStatus();
      } else {
        toasterApi.error({
          title: t('removeFailedTitle'),
          description: result.error || t('removeFailedDescription'),
        });
      }
    } catch (error) {
      toasterApi.error({
        title: t('removeFailedTitle'),
        description: String(error),
      });
    } finally {
      setIsRemovingCert(false);
    }
  };

  const handleReinstallCertificate = async () => {
    try {
      const result = await installCACertificate();

      if (result.success) {
        toasterApi.info({
          title: t('installStartedTitle'),
          description: t('installStartedDescription'),
        });
        setTimeout(() => {
          void checkCertStatus();
        }, CERTIFICATE_STATUS_RECHECK_DELAY_MS);
      } else {
        toasterApi.error({
          title: t('installFailedTitle'),
          description: result.error || t('installFailedDescription'),
        });
      }
    } catch (error) {
      toasterApi.error({
        title: t('installFailedTitle'),
        description: String(error),
      });
    }
  };

  const renderHostRows = ({
    setting,
    emptyText,
    removeLabel,
  }: {
    setting: HostSetting;
    emptyText: string;
    removeLabel: string;
  }) =>
    renderProxySettingsListRows({
      items: (settings[setting] || []).map((host: string, index: number) => ({
        key: String(index),
        label: host,
      })),
      emptyText,
      removeLabel,
      onRemove: (key) => removeFromArraySetting(setting, Number(key)),
    });

  const addHostButton = (onClick: () => void) => (
    <Button size="xs" variant="outline" onClick={onClick}>
      <Add01Icon size={14} />
      {tCommon('add')}
    </Button>
  );

  return (
    <>
      <VStack gap={6} alignItems="stretch" w="full">
        <ProxySettingsGroup>
          <SettingsRow
            label={t('requireAuth')}
            description={t('requireAuthDescription')}
            control={
              <SettingsBooleanControl
                checked={settings.requireAuth}
                onCheckedChange={(details) =>
                  updateSetting('requireAuth', details.checked)
                }
              />
            }
          />
        </ProxySettingsGroup>

        {settings.requireAuth ? (
          <ProxySettingsGroup title={t('credentialsGroup')}>
            <ProxySettingEditRow
              kind="text"
              label={t('username')}
              value={settings.authCredentials?.username || ''}
              onSave={(value) =>
                updateNestedSetting('authCredentials', 'username', value)
              }
              placeholder="admin"
            />
            <ProxySettingEditRow
              kind="password"
              label={t('password')}
              value={settings.authCredentials?.password || ''}
              onSave={(value) =>
                updateNestedSetting('authCredentials', 'password', value)
              }
              placeholder="password"
            />
          </ProxySettingsGroup>
        ) : null}

        <ProxySettingsGroup
          title={t('certificateGroup')}
          description={t('certificateGroupDescription', { brandName })}
        >
          <SettingsRow
            label={t('certificateStatus')}
            description={isCertInstalled ? t('installed') : t('notInstalled')}
            control={
              <HStack gap={2}>
                <Button
                  size="xs"
                  variant="outline"
                  onClick={handleReinstallCertificate}
                  loading={isInstallingCert}
                >
                  <SecurityCheckIcon size={14} />
                  {isCertInstalled ? t('reinstall') : t('install')}
                </Button>
                <Button
                  size="xs"
                  variant="outline"
                  onClick={() => setShowConfirmDialog(true)}
                  loading={isRemovingCert}
                  disabled={!isCertInstalled}
                >
                  <ShieldBanIcon size={14} />
                  {t('remove')}
                </Button>
              </HStack>
            }
          />
        </ProxySettingsGroup>

        <ProxySettingsGroup
          title={t('allowedHosts')}
          action={addHostButton(() => setHostDialogSetting('allowedHosts'))}
        >
          {renderHostRows({
            setting: 'allowedHosts',
            emptyText: t('allowedEmpty'),
            removeLabel: tSecurity('removeAllowedHost'),
          })}
        </ProxySettingsGroup>

        <ProxySettingsGroup
          title={t('blockedHosts')}
          action={addHostButton(() => setHostDialogSetting('blockedHosts'))}
        >
          {renderHostRows({
            setting: 'blockedHosts',
            emptyText: t('blockedEmpty'),
            removeLabel: tSecurity('removeBlockedHost'),
          })}
        </ProxySettingsGroup>
      </VStack>

      <ProxyValueAddDialog
        open={hostDialogSetting !== null}
        title={
          hostDialogSetting === 'blockedHosts'
            ? t('addBlockedTitle')
            : t('addAllowedTitle')
        }
        label={t('hostLabel')}
        placeholder={
          hostDialogSetting === 'blockedHosts'
            ? t('blockedPlaceholder')
            : t('allowedPlaceholder')
        }
        onAdd={(host) => {
          if (hostDialogSetting) {
            addToArraySetting(hostDialogSetting, host);
          }
        }}
        onClose={() => setHostDialogSetting(null)}
      />

      <ConfirmationDialog
        isOpen={showConfirmDialog}
        onOpenChange={(details) => setShowConfirmDialog(details.open)}
        onConfirm={confirmRemoveCertificate}
        loading={isRemovingCert}
        variant="warning"
        title={t('removeDialogTitle')}
        description={t('removeDialogDescription', { brandName })}
        confirmText={t('removeDialogConfirm')}
        cancelText={tCommon('cancel')}
      />
    </>
  );
}
